import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import DashLayout from '../../components/DashLayout';
import { useAuth } from '../../context/AuthContext';

const nav = [
  { to: '/dashboard', icon: '📊', label: 'Dashboard' },
  { to: '/dashboard/chats', icon: '💬', label: 'Live Chats' },
  { to: '/dashboard/leads', icon: '👥', label: 'Leads' },
  { to: '/dashboard/crm', icon: '📋', label: 'CRM' },
  { to: '/dashboard/agents', icon: '👤', label: 'Agents' },
  { to: '/dashboard/tickets', icon: '🎫', label: 'Support Tickets' },
  { to: '/dashboard/ai-bot', icon: '🤖', label: 'AI Chat Bot' },
  { to: '/dashboard/api', icon: '🔑', label: 'API Access' },
  { to: '/dashboard/branding', icon: '🎨', label: 'Branding' },
  { to: '/dashboard/widget', icon: '🔌', label: 'Widget & Script' },
  { to: '/dashboard/contact-form', icon: '📝', label: 'Contact Form' },
  { to: '/dashboard/analytics', icon: '📈', label: 'Analytics' },
  { to: '/dashboard/billing', icon: '💳', label: 'Billing' },
  { to: '/dashboard/settings', icon: '⚙️', label: 'Settings' }
];

const STATUS_COLORS = { open: '#f59e0b', in_progress: '#3b82f6', resolved: '#00d55b', closed: '#555' };
const CATEGORIES = ['General', 'Billing', 'Support'];

export default function Tickets() {
  const { user } = useAuth();
  const [tickets, setTickets] = useState([]);
  const [selected, setSelected] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [reply, setReply] = useState('');
  const [form, setForm] = useState({ subject: '', category: 'General', message: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => { loadTickets(); }, []);

  // Auto refresh tickets list every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => loadTickets(), 10000);
    return () => clearInterval(interval);
  }, []);

  // Auto refresh selected ticket every 5 seconds
  useEffect(() => {
    if (!selected) return;
    const interval = setInterval(async () => {
      const r = await axios.get(`/api/tickets/${selected.id}`).catch(() => null);
      if (r?.data?.data) setSelected(r.data.data);
    }, 5000);
    return () => clearInterval(interval);
  }, [selected?.id]);

  const loadTickets = () => {
    axios.get('/api/tickets').then(r => setTickets(r.data.data || [])).catch(() => {});
  };

  const loadTicket = async (ticket) => {
    const r = await axios.get(`/api/tickets/${ticket.id}`);
    setSelected(r.data.data);
  };

  const createTicket = async () => {
    if (!form.subject || !form.message) return toast.error('Fill all fields');
    setLoading(true);
    try {
      await axios.post('/api/tickets', form);
      toast.success('Ticket created!');
      setShowNew(false);
      setForm({ subject: '', category: 'General', message: '' });
      loadTickets();
    } catch (err) { toast.error('Failed'); }
    finally { setLoading(false); }
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    await axios.post(`/api/tickets/${selected.id}/reply`, { message: reply });
    toast.success('Reply sent!');
    setReply('');
    loadTicket(selected);
  };

  return (
    <DashLayout navItems={nav} role="Business Admin">
      <div style={{ padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700 }}>Support Tickets</h1>
            <p style={{ color: '#555', fontSize: 13, marginTop: 4 }}>Raise and track support requests</p>
          </div>
          <button onClick={() => setShowNew(true)}
            style={{ padding: '10px 20px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer', border: 'none' }}>
            + New Ticket
          </button>
        </div>

        {/* New Ticket Modal */}
        {showNew && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 28, width: '100%', maxWidth: 520 }}>
              <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 20 }}>Create New Ticket</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Category</label>
                  <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Subject *</label>
                  <input value={form.subject} onChange={e => setForm(f => ({ ...f, subject: e.target.value }))} placeholder="Brief description of your issue" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#888', display: 'block', marginBottom: 5 }}>Message *</label>
                  <textarea value={form.message} onChange={e => setForm(f => ({ ...f, message: e.target.value }))} rows={4} placeholder="Describe your issue in detail..." style={{ resize: 'vertical' }} />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setShowNew(false)} style={{ flex: 1, padding: '10px', background: '#1a1a1a', border: '1px solid #1e1e1e', color: '#fff', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
                  <button onClick={createTicket} disabled={loading} style={{ flex: 2, padding: '10px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
                    {loading ? 'Creating...' : 'Submit Ticket'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: selected ? '1fr 420px' : '1fr', gap: 20 }}>
          {/* Ticket list */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {tickets.length === 0 ? (
              <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 12, padding: 40, textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🎫</div>
                <div style={{ color: '#555', fontSize: 14 }}>No tickets yet. Create one if you need help!</div>
              </div>
            ) : tickets.map(ticket => (
              <div key={ticket.id} onClick={() => loadTicket(ticket)}
                style={{ background: selected?.id === ticket.id ? 'rgba(0,213,91,0.05)' : '#111', border: `1px solid ${selected?.id === ticket.id ? '#00d55b' : '#1e1e1e'}`, borderRadius: 12, padding: '16px 20px', cursor: 'pointer' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, padding: '2px 8px', background: '#1a1a1a', borderRadius: 20, color: '#888' }}>{ticket.category}</span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: `${STATUS_COLORS[ticket.status]}22`, color: STATUS_COLORS[ticket.status], fontWeight: 600 }}>
                      {ticket.status?.replace('_', ' ')}
                    </span>
                  </div>
                  <span style={{ fontSize: 11, color: '#555' }}>{new Date(ticket.created_at).toLocaleDateString('en-IN')}</span>
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#fff', marginBottom: 4 }}>#{ticket.id} — {ticket.subject}</div>
                <div style={{ fontSize: 12, color: '#555' }}>{ticket.reply_count || 0} replies</div>
              </div>
            ))}
          </div>

          {/* Ticket detail */}
          {selected && (
            <div style={{ background: '#111', border: '1px solid #1e1e1e', borderRadius: 16, padding: 20, height: 'fit-content', position: 'sticky', top: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>#{selected.id} — {selected.subject}</div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <span style={{ fontSize: 11, padding: '2px 8px', background: '#1a1a1a', borderRadius: 20, color: '#888' }}>{selected.category}</span>
                    <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: `${STATUS_COLORS[selected.status]}22`, color: STATUS_COLORS[selected.status], fontWeight: 600 }}>
                      {selected.status?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
                <button onClick={() => setSelected(null)} style={{ background: 'none', border: 'none', color: '#555', cursor: 'pointer', fontSize: 18 }}>✕</button>
              </div>

              {/* Messages */}
              <div style={{ maxHeight: 350, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {(selected.replies || []).map((r, i) => (
                  <div key={i} style={{ padding: '10px 14px', borderRadius: 10, background: r.sender_type === 'business' ? 'rgba(0,213,91,0.08)' : '#1a1a1a', border: `1px solid ${r.sender_type === 'business' ? 'rgba(0,213,91,0.2)' : '#1e1e1e'}`, alignSelf: r.sender_type === 'business' ? 'flex-end' : 'flex-start', maxWidth: '90%' }}>
                    <div style={{ fontSize: 11, color: '#555', marginBottom: 4 }}>{r.sender_type === 'business' ? 'You' : 'MS Discoveries Support'} · {new Date(r.created_at).toLocaleString('en-IN')}</div>
                    <div style={{ fontSize: 13, color: '#ddd', lineHeight: 1.5 }}>{r.message}</div>
                  </div>
                ))}
              </div>

              {/* Reply */}
              {selected.status !== 'closed' && (
                <div>
                  <textarea value={reply} onChange={e => setReply(e.target.value)} placeholder="Type your reply..." rows={3}
                    style={{ width: '100%', background: '#0d0d0d', border: '1px solid #1e1e1e', borderRadius: 8, color: '#fff', fontSize: 13, padding: '8px 12px', resize: 'none', outline: 'none', fontFamily: 'inherit', marginBottom: 8 }} />
                  <button onClick={sendReply}
                    style={{ width: '100%', padding: '10px', background: '#00d55b', color: '#000', borderRadius: 8, fontWeight: 700, fontSize: 14, border: 'none', cursor: 'pointer' }}>
                    Send Reply
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </DashLayout>
  );
}